import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { CircleNotch, Rocket, Plus, Trash, Cpu, Users } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const T = '#007BFC';

const VultrNew = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [meta, setMeta] = useState({ regions: [], plans: [], windows_images: [] });
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [form, setForm] = useState({ account_id: '', region: '', plan: '', os_id: '', label: '', enable_multiuser: true });
  const [users, setUsers] = useState([{ username: 'user1' }, { username: 'user2' }, { username: 'user3' }, { username: 'user4' }]);
  const [deploying, setDeploying] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/vultr-rdp/accounts`, { withCredentials: true }).then(r => {
      const ready = r.data.filter(a => a.status === 'ready'); setAccounts(r.data);
      if (ready.length) setForm(f => ({ ...f, account_id: ready[0].id }));
    });
  }, []);

  useEffect(() => {
    if (!form.account_id) return;
    setLoadingMeta(true);
    axios.get(`${API}/api/vultr-rdp/accounts/${form.account_id}/meta`, { withCredentials: true })
      .then(r => {
        setMeta(r.data);
        const win = r.data.windows_images?.[0];
        setForm(f => ({ ...f, region: r.data.regions?.[0]?.id || '', os_id: win ? String(win.os_id) : '' }));
      }).catch(e => toast.error(e.response?.data?.detail || 'Meta load failed')).finally(() => setLoadingMeta(false));
  }, [form.account_id]);

  // plans available in selected region, >=8GB
  const plans = meta.plans.filter(p => (!form.region || (p.locations || []).includes(form.region)) && (p.ram_gb || 0) >= 8);
  const selPlan = meta.plans.find(p => p.id === form.plan);

  const deploy = async () => {
    if (!form.account_id || !form.region || !form.plan || !form.os_id) return toast.error('Choose account, region, plan and Windows image');
    setDeploying(true);
    try {
      const body = { account_id: form.account_id, region: form.region, plan: form.plan, os_id: Number(form.os_id),
        os_name: (meta.windows_images.find(w => String(w.os_id) === String(form.os_id)) || {}).name,
        label: form.label || 'rdp', enable_multiuser: form.enable_multiuser,
        extra_users: users.filter(u => u.username.trim()).map(u => ({ username: u.username.trim() })) };
      await axios.post(`${API}/api/vultr-rdp/vms`, body, { withCredentials: true });
      toast.success('Provisioning started on Vultr'); navigate('/vultr/machines');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setDeploying(false); }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500 max-w-4xl">
      <div><h1 className="text-4xl font-black mb-2" style={{ color: T }}>NEW VULTR RDP</h1>
        <p className="text-slate-400 font-mono">Provision a Windows Server RDP instance on Vultr.</p></div>
      {accounts.filter(a => a.status === 'ready').length === 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 p-4 text-sm">No tested Vultr account. Add & test one under <button className="underline" onClick={() => navigate('/vultr/accounts')}>Vultr Accounts</button>.</div>
      )}
      <div className="bg-card border border-white/10 p-6 space-y-5">
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Vultr Account</label>
          <select value={form.account_id} onChange={e => setForm({ ...form, account_id: e.target.value })} data-testid="vultr-account-select" className="w-full bg-[#0A0B0E] border border-white/10 p-3 text-white focus:outline-none">
            <option value="">-- choose --</option>
            {accounts.map(a => <option key={a.id} value={a.id} disabled={a.status !== 'ready'}>{a.label}{a.status !== 'ready' ? ' (not tested)' : ''}</option>)}
          </select>
        </div>
        {loadingMeta ? <div className="flex items-center gap-2 text-slate-400"><CircleNotch size={18} className="animate-spin" /> Loading Vultr regions & plans…</div> : (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">EU Region</label>
                <select value={form.region} onChange={e => setForm({ ...form, region: e.target.value, plan: '' })} data-testid="vultr-region-select" className="w-full bg-[#0A0B0E] border border-white/10 p-3 text-white focus:outline-none">
                  {meta.regions.map(r => <option key={r.id} value={r.id}>{r.city}, {r.country} ({r.id})</option>)}
                </select></div>
              <div><label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Windows Image</label>
                <select value={form.os_id} onChange={e => setForm({ ...form, os_id: e.target.value })} data-testid="vultr-image-select" className="w-full bg-[#0A0B0E] border border-white/10 p-3 text-white focus:outline-none">
                  {meta.windows_images.map(w => <option key={w.os_id} value={w.os_id}>{w.name}</option>)}
                </select></div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Plan (RAM / vCPU)</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-64 overflow-y-auto">
                {plans.map(p => (
                  <button key={p.id} onClick={() => setForm({ ...form, plan: p.id })} data-testid={`vultr-plan-${p.id}`}
                    className={`text-left p-3 border transition-colors ${form.plan === p.id ? 'border-[#007BFC] bg-[#007BFC]/10' : 'border-white/10 hover:border-white/30'}`}>
                    <div className="flex items-center gap-2 font-bold text-white text-sm"><Cpu size={16} style={{ color: T }} /> {p.ram_gb} GB</div>
                    <div className="text-xs text-slate-400 mt-1">{p.vcpu} vCPU · {p.disk_gb} GB SSD</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{p.id}</div>
                    <div className="text-xs font-bold mt-1" style={{ color: T }}>${p.monthly_cost}/mo</div>
                  </button>
                ))}
                {plans.length === 0 && <div className="text-slate-500 text-sm col-span-3">No Windows plans in this region. Pick another region.</div>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Name (optional)</label>
                <input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="auto" data-testid="vultr-name-input" className="w-full bg-[#0A0B0E] border border-white/10 p-3 text-white focus:outline-none" /></div>
              <label className="flex items-center gap-2 mt-7 cursor-pointer text-sm text-slate-300">
                <input type="checkbox" checked={form.enable_multiuser} onChange={e => setForm({ ...form, enable_multiuser: e.target.checked })} data-testid="vultr-multiuser" className="accent-[#007BFC] w-4 h-4" />
                Enable multi-user concurrent RDP (RDS)
              </label>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold flex items-center gap-2"><Users size={16} style={{ color: T }} /> Extra RDP Users</label>
                <button onClick={() => setUsers([...users, { username: '' }])} data-testid="vultr-add-user-row" className="flex items-center gap-1 text-xs border border-white/15 px-2 py-1 text-slate-300 hover:bg-white/5"><Plus size={14} /> Add</button>
              </div>
              {users.map((u, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input value={u.username} onChange={e => { const n = [...users]; n[i].username = e.target.value; setUsers(n); }} placeholder="username" data-testid={`vultr-user-${i}`} className="flex-1 bg-[#0A0B0E] border border-white/10 p-2 text-white text-sm focus:outline-none" />
                  <button onClick={() => setUsers(users.filter((_, j) => j !== i))} className="text-red-400 px-2"><Trash size={16} /></button>
                </div>
              ))}
              <p className="text-[11px] text-slate-500">Admin = <b>Administrator</b> (Vultr auto-password, shown after install). Extra users get auto-generated passwords. RDP (3389) opens to everyone.</p>
            </div>
            <button onClick={deploy} disabled={deploying || !form.plan} data-testid="vultr-deploy-btn" className="w-full flex items-center justify-center gap-2 text-white py-4 font-black uppercase tracking-wider disabled:opacity-40" style={{ background: T }}>
              {deploying ? <CircleNotch size={20} className="animate-spin" /> : <Rocket size={20} />} Provision {selPlan ? `${selPlan.ram_gb}GB` : ''} Windows RDP
            </button>
          </>
        )}
      </div>
    </div>
  );
};
export default VultrNew;
